import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { RootState } from '../../redux/store'
import { fetchTournamentDetails } from '../../redux/slices/tournamentDetailesSlice'
import { Smartico } from '../../core/Smartico'
import { AchRelatedGame } from '@smartico/public-api/dist/WSAPI/WSAPITypes'
import SectionWrapper from '../../UI-components/Sections/SectionWrapper/SectionWrapper'
import Utils from '../../core/Utils'
import { Link } from 'react-router-dom'
import { Texts, useTranslation } from '../../hooks/useTranslation'
import { Player as LottiePlayer } from '@lottiefiles/react-lottie-player'
import animationCar from './animations/carsparkle.json'
import { TournamentRegistrationStatusName, TournamentRegistrationType, TournamentRegistrationTypeGetName } from '@smartico/public-api'

interface TimeLeft {
	days: number
	hours: number
	minutes: number
	seconds: number
}
interface Tournament {
	is_upcoming: boolean
	is_in_progress: boolean
	is_cancelled: boolean
	is_finished: boolean
}

interface LeaderboardTabProps {
	players: Player[]
	prizes: Prize[]
	currentUser: Player | undefined
}

interface Prize {
	/** The name of the prize */
	name: string
	/** The description of the prize */
	description: string
	/** The image of the prize */
	image_url: string
	/** from-to range of the places to which this prize */
	place_from: number
	place_to: number
	/** type of the prize: TANGIBLE, POINTS_ADD, POINTS_DEDUCT, POINTS_RESET, MINI_GAME_ATTEMPT, BONUS */
	type: string
	/** if the prize is points related, indicates amount of points */
	points?: number
}

interface MoreInfoTabProps {
	description: string
}

interface RelatedGamesProps {
	relatedGames: AchRelatedGame[]
}

interface TabChangeHandler {
	(newTab: string): void
}

interface Player {
	is_me: boolean
	avatar_url: string
	public_username: string
	scores: number
	position: number
}

const statusClazz = (tournament: Tournament | undefined): string => {
	if (tournament) {
		if (tournament.is_upcoming) return 'gathering'
		if (tournament.is_in_progress) return 'started'
		if (tournament.is_cancelled) return 'cancelled'
		if (tournament.is_finished) return 'finished'
	}
	return ''
}

const TournamentDetailsPage = () => {
	const isMobile = Utils.isMobile()
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const { instance_id } = useParams<{ instance_id: string }>()
	const tournamentInstanceId = Number(instance_id)

	const tournamentInfo = useAppSelector((state: RootState) => state.tournamentInfo.details[tournamentInstanceId])
	const tournaments = useAppSelector((state: RootState) => state.tournaments.tournaments)
	const currentTournament = tournaments.find((tournament) => tournament.instance_id === tournamentInstanceId)

	const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 })
	const [activeTab, setActiveTab] = useState('leaderboard')

	const { getTexts } = useTranslation(true)

	useEffect(() => {
		dispatch(fetchTournamentDetails(tournamentInstanceId))
	}, [dispatch, tournamentInstanceId])

	useEffect(() => {
		if (!tournamentInfo) {
			return
		}
		const calculateTimeLeft = (targetTime: number): TimeLeft => {
			const now = new Date().getTime()
			const timeDiff = targetTime - now

			if (timeDiff > 0) {
				const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
				const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
				const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60))
				const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000)
				return { days, hours, minutes, seconds }
			} else {
				return { days: 0, hours: 0, minutes: 0, seconds: 0 }
			}
		}

		const calculateTime = () => {
			if (tournamentInfo?.is_in_progress) {
				const endTime = new Date(tournamentInfo.end_time).getTime()
				setTimeLeft(calculateTimeLeft(endTime))
			} else if (tournamentInfo?.is_upcoming) {
				const startTime = new Date(tournamentInfo.start_time).getTime()
				setTimeLeft(calculateTimeLeft(startTime))
			} else {
				setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 })
			}
		}

		const interval = tournamentInfo?.is_in_progress ? setInterval(calculateTime, 1000) : null

		return () => {
			if (interval) {
				clearInterval(interval)
			}
		}
	}, [tournamentInfo])

	if (!tournamentInfo) {
		return <div className="main-content tournament-page">Tournament not found!</div>
	}

	const changeTab: TabChangeHandler = (newTab) => {
		setActiveTab(newTab)
	}

	const renderTabContent = () => {
		switch (activeTab) {
			case 'leaderboard':
				return tournamentInfo.players ? (
					<LeaderboardTab players={tournamentInfo.players} prizes={tournamentInfo.prizes || []} currentUser={tournamentInfo.me ? { ...tournamentInfo.me, is_me: true } : undefined} />
				) : null
			case 'info':
				return <MoreInfoTab description={tournamentInfo.description} />
			default:
				return null
		}
	}

	const fromatDate = (time: number) => {
		const date = new Date(time)
		return date.toLocaleDateString()
	}

	const addLeadingZero = (number: number): string => (number < 10 ? `0${number}` : number.toString())

	const handleJoinTournament = async () => {
		try {
			await (await Smartico.apiReady).registerInTournament(tournamentInstanceId)

			dispatch(fetchTournamentDetails(tournamentInstanceId))
		} catch (error) {
			console.error('Error joining the tournament:', error)
		}
	}
	const statusKey = statusClazz(tournamentInfo) as keyof Texts
	return (
		<>
			{tournamentInfo ? (
				<div className="main-content tournament-page">
					{tournamentInfo.ribbon && <div className={`ribbon ${tournamentInfo.ribbon}`}>{tournamentInfo.ribbon}</div>}
					<div className="tournament-header">
						<div className="tournament-title-wrapper">
							<div className="back-btn" onClick={() => navigate(-1)}></div>
							<div className="title" dangerouslySetInnerHTML={{ __html: tournamentInfo.name }}></div>
							<div className="status-wrapper">
								<div className={`status ${statusKey}`}>{statusKey && getTexts(statusKey)}</div>
							</div>
						</div>
					</div>
					<div className="tournament-info-block">
						<div className="wrapped-info">
							<div className="time">
								<div className="number">{addLeadingZero(timeLeft.days)}</div>
								<div className="dots">:</div>
								<div className="number">{addLeadingZero(timeLeft.hours)}</div>
								<div className="dots">:</div>
								<div className="number">{addLeadingZero(timeLeft.minutes)}</div>
								<div className="dots">:</div>
								<div className="number">{addLeadingZero(timeLeft.seconds)}</div>
							</div>
							<div className="info-blocks">
								<div className="start-end-dates">
									{fromatDate(tournamentInfo.start_time)} - {fromatDate(tournamentInfo.end_time)}
								</div>
								<div className="blocks">
									<div className="prize">
										<div className="headline">{getTexts('prize')}</div>
										<div className="value">{tournamentInfo.prize_pool_short ? tournamentInfo.prize_pool_short : 'No prize'}</div>
									</div>
									<div className="registered">
										<div className="headline">{getTexts('registered')}</div>
										<div className="value">
											{tournamentInfo.registration_count}/{tournamentInfo.players_max_count}
										</div>
									</div>
									<div className="buy-in">
										<div className="headline">{getTexts('buy_in')}</div>
										<div className="value">{Utils.formatTournamentBuyIn(tournamentInfo)}</div>
									</div>
								</div>
							</div>
						</div>
						{tournamentInfo.registration_status !== TournamentRegistrationStatusName.REGISTERED ? (
							<>
								{tournamentInfo.registration_type !== TournamentRegistrationTypeGetName(TournamentRegistrationType.AUTO) ? (
									<div className="btn" onClick={handleJoinTournament}>
										{getTexts('join_tournament')}
									</div>
								) : (
									<div style={{ marginTop: 'auto', fontWeight: 900 }}>{getTexts('auto_optin')}</div>
								)}
							</>
						) : (
							<div>{getTexts('registered')}</div>
						)}
						<div
							className="big-prize-image"
							style={{
								backgroundImage: `url('${currentTournament?.custom_data.extra_image}')`,
							}}
						>
							<LottiePlayer autoplay loop src={animationCar} style={{ height: '100%', width: '100%', position: 'absolute', top: '0', left: '0' }} />
						</div>
					</div>
					<div className="tournament-body">
						<div className="tabs">
							<div className="tabs-nav">
								<div onClick={() => changeTab('leaderboard')} className={`tab ${activeTab === 'leaderboard' ? 'active' : ''}`}>
									{getTexts('leaderboard')}
								</div>
								<div onClick={() => changeTab('info')} className={`tab ${activeTab === 'info' ? 'active' : ''}`}>
									{getTexts('more_info')}
								</div>
							</div>
							<div className="tab-content">{renderTabContent()}</div>
						</div>

						{tournamentInfo.related_games && tournamentInfo.related_games.length > 0 && (
							<>
								{!isMobile ? (
									<RelatedGames relatedGames={tournamentInfo.related_games || []} />
								) : (
									<SectionWrapper title={getTexts('related_games')}>
										{tournamentInfo.related_games.map((game, gameIndex) => (
											<div className="related-game" style={{ backgroundImage: `url('${game.game_public_meta.image}')` }} key={gameIndex}>
												<div className="game-overlay">
													<Link to={`/game/${game.ext_game_id}`}>
														<div className="btn">{getTexts('play')}</div>
													</Link>
												</div>
											</div>
										))}
									</SectionWrapper>
								)}
							</>
						)}
					</div>
				</div>
			) : (
				<div>{getTexts('no_tournaments_loaded')}</div>
			)}
		</>
	)
}

const getPlaceClass = (rank: number | undefined) => {
	switch (rank) {
		case 1:
			return 'first-place'
		case 2:
			return 'second-place'
		case 3:
			return 'third-place'
		default:
			return ''
	}
}

export const LeaderboardTab: React.FC<LeaderboardTabProps> = ({ players, prizes, currentUser }) => {
	const { getTexts } = useTranslation(true)
	const [currentPage, setCurrentPage] = useState(1)
	const playersPerPage = 10

	const totalPages = Math.ceil(players.length / playersPerPage)

	const startIndex = (currentPage - 1) * playersPerPage
	const endIndex = startIndex + playersPerPage
	const paginatedPlayers = players.slice(startIndex, endIndex)

	const handlePageChange = (newPage: number) => {
		if (newPage >= 1 && newPage <= totalPages) {
			setCurrentPage(newPage)
		}
	}

	const generatePageNumbers = () => {
		const pageNumbers = []
		const maxPagesToShow = 5

		if (totalPages <= maxPagesToShow) {
			for (let i = 1; i <= totalPages; i++) {
				pageNumbers.push(i)
			}
		} else {
			if (currentPage <= 3) {
				pageNumbers.push(1, 2, 3, 4, '...', totalPages)
			} else if (currentPage >= totalPages - 2) {
				pageNumbers.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
			} else {
				pageNumbers.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages)
			}
		}

		return pageNumbers
	}

	return (
		<>
			{players.length > 0 ? (
				<div className="leaderboard-tab">
					<div className="leaders-list">
						<div className="leaders-header">
							<div className="rank">{getTexts('rank')}</div>
							<div className="player">{getTexts('player')}</div>
							<div className="score">{getTexts('score')}</div>
						</div>
						{paginatedPlayers.map((player, index) => {
							const playerPrize = prizes && prizes.find((prize) => prize.place_from <= player.position && prize.place_to >= player.position)
							return (
								<div className={`player-row`} key={index}>
									<div className="player-rank">{player.position}</div>
									<div className="player-details">
										<div className="player-avatar" style={player.avatar_url ? { backgroundImage: `url('${player.avatar_url}')` } : {}}></div>
										<div className="player-name">{player.public_username}</div>
									</div>
									<div className="score-block">
										<div className={`position-prize ${getPlaceClass(player.position)}`}>{playerPrize?.name}</div>
										<div className="player-score">{player.scores}pts</div>
									</div>
								</div>
							)
						})}
						{currentUser && (
							<div className={`player-row my-user`} key="current-user">
								<div className="player-rank">{currentUser.position}</div>
								<div className="player-details">
									<div className="player-avatar" style={currentUser.avatar_url ? { backgroundImage: `url('${currentUser.avatar_url}')` } : {}}></div>
									<div className="player-name">{currentUser.public_username}</div>
								</div>
								<div className="score-block">
									<div className={`position-prize ${getPlaceClass(currentUser.position)}`}>{prizes && prizes.find((prize) => prize.place_to === currentUser.position)?.name}</div>
									<div className="player-score">{currentUser.scores}pts</div>
								</div>
							</div>
						)}
					</div>

					<div className="pagination-controls">
						<div className={`page-control previous ${currentPage === 1 ? 'disabled' : ''}`} onClick={() => handlePageChange(currentPage - 1)}>
							<div className="icon"></div>
						</div>

						{generatePageNumbers().map((page, index) =>
							typeof page === 'number' ? (
								<div key={index} className={`page-number ${page === currentPage ? 'active' : ''}`} onClick={() => handlePageChange(page)}>
									{page}
								</div>
							) : (
								<div key={index} className="ellipsis">
									{page}
								</div>
							),
						)}

						<div className={`page-control next ${currentPage === totalPages ? 'disabled' : ''}`} onClick={() => handlePageChange(currentPage + 1)}>
							<div className="icon"></div>
						</div>
					</div>
				</div>
			) : (
				<div className="leaderboard-tab no-players">{getTexts('no_players_registered')}</div>
			)}
		</>
	)
}

const MoreInfoTab: React.FC<MoreInfoTabProps> = ({ description }) => {
	// Content for the More Info tab goes here, it is set from the Back Office
	return <div style={{ color: '#FFF' }} dangerouslySetInnerHTML={{ __html: description }}></div>
}

const RelatedGames: React.FC<RelatedGamesProps> = ({ relatedGames }) => {
	const { getTexts } = useTranslation(true)
	return (
		<div className="related-games">
			<div className="games-header">
				<div className="title">{getTexts('related_games')}</div>
			</div>
			<div className="game-list">
				{relatedGames.map((game, gameIndex) => (
					<Link to={game.game_public_meta.link} className="related-game" style={{ backgroundImage: `url('${game.game_public_meta.image}')` }} key={gameIndex}></Link>
				))}
			</div>
		</div>
	)
}

export default TournamentDetailsPage
